import { API_LABEL, type PartnerActivityEntry } from "../model";

/**
 * Column metadata for the per-partner Recent Activity table — the same
 * shape as `metricsColumns.ts`'s `METRICS_COLUMNS`, one descriptor per
 * column, including the value used for sorting.
 */

interface ActivityColumnDef {
  readonly id: string;
  readonly label: string;
  readonly numeric: boolean;
  readonly sortable: boolean;
  readonly sortValue: (entry: PartnerActivityEntry) => string | number | null;
  readonly sortAscByDefault: boolean;
  readonly tileSlot: "title" | "body" | "status";
  readonly project: (entry: PartnerActivityEntry) => ActivityCellProjection;
}

export type ActivityCellProjection =
  | { kind: "text"; text: string }
  | { kind: "activity-status"; status: PartnerActivityEntry["status"] };

export const ACTIVITY_COLUMNS = [
  {
    id: "time",
    sortValue: (entry) => entry.time,
    label: "Time",
    numeric: false,
    tileSlot: "title",
    sortable: true,
    sortAscByDefault: false,
    project: (entry) => ({ kind: "text", text: entry.time }),
  },
  {
    id: "type",
    sortValue: (entry) => entry.type,
    label: "Type",
    numeric: false,
    tileSlot: "body",
    sortable: true,
    sortAscByDefault: true,
    project: (entry) => ({ kind: "text", text: API_LABEL[entry.type] }),
  },
  {
    id: "query",
    sortValue: (entry) => entry.query,
    label: "Query / Endpoint",
    numeric: false,
    tileSlot: "body",
    sortable: true,
    sortAscByDefault: true,
    project: (entry) => ({ kind: "text", text: entry.query }),
  },
  {
    id: "results",
    sortValue: (entry) => entry.resultCount,
    label: "Results",
    numeric: true,
    tileSlot: "body",
    sortable: true,
    sortAscByDefault: true,
    project: (entry) => ({
      kind: "text",
      text: entry.resultCount === null ? "—" : entry.resultCount.toLocaleString(),
    }),
  },
  {
    id: "region",
    sortValue: (entry) => entry.region,
    label: "Region",
    numeric: false,
    tileSlot: "body",
    sortable: true,
    sortAscByDefault: true,
    project: (entry) => ({ kind: "text", text: entry.region }),
  },
  {
    id: "status",
    sortValue: (entry) => entry.status,
    label: "Status",
    numeric: false,
    tileSlot: "status",
    sortable: true,
    sortAscByDefault: true,
    project: (entry) => ({ kind: "activity-status", status: entry.status }),
  },
] as const satisfies readonly ActivityColumnDef[];

export type ActivityColumn = (typeof ACTIVITY_COLUMNS)[number];
export type ActivityColumnId = ActivityColumn["id"];
/** Derived from which entries are marked `sortable: true` — not a second, hand-written list. */
export type ActivitySortColumn = Extract<ActivityColumn, { sortable: true }>["id"];

export interface ActivitySort {
  column: ActivitySortColumn;
  direction: "asc" | "desc";
}

const COLUMN_BY_ID = Object.fromEntries(
  ACTIVITY_COLUMNS.map((column) => [column.id, column])
) as Record<ActivityColumnId, ActivityColumn>;

export function activityColumn(id: ActivityColumnId): ActivityColumn {
  return COLUMN_BY_ID[id];
}

export function isActivitySortColumn(id: ActivityColumnId): id is ActivitySortColumn {
  return activityColumn(id).sortable;
}
